"use client";

import { Trophy } from "lucide-react";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { SweetCoin } from "@/components/coins/SweetCoin";
import { useLang, useUi } from "@/components/ui/use-ui";
import { announceAchievements, type Unlocked } from "@/lib/activity";
import { cn } from "@/lib/cn";
import { localePath } from "@/lib/i18n";
import { fmt } from "@/lib/locales/fmt";
import { refreshCoins } from "@/lib/coins-badge";

// The star of the month vote on a star's page (lib/star-votes.ts via /api/stars/vote): the day's free vote,
// then more for coins after a second tap to confirm; this month's votes beside it. The page itself stays
// cached: the counts and the member's votes come from the browser.

interface VoteState {
  votes: number;
  rank: number | null;
  free: boolean;
  paidLeft: number;
  balance: number | null;
  cost: number;
}

export function StarVote({ id, slug, name }: { id: string; slug: string; name: string }) {
  const t = useUi();
  const lang = useLang();
  const router = useRouter();
  const [state, setState] = useState<VoteState | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    fetch(`/api/stars/vote?id=${id}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((s: VoteState | null) => live && s && setState(s))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [id]);

  useEffect(() => {
    if (!note) return;
    const timer = window.setTimeout(() => setNote(null), 3500);
    return () => window.clearTimeout(timer);
  }, [note]);

  useEffect(() => {
    if (!confirming) return;
    const timer = window.setTimeout(() => setConfirming(false), 4000);
    return () => window.clearTimeout(timer);
  }, [confirming]);

  const guest = state?.balance === null;
  const paying = !!state && !state.free;
  const done = paying && state.paidLeft === 0;
  const cost = state?.cost ?? 0;

  const vote = async () => {
    if (!state || busy) return;
    if (guest) return router.push(localePath(lang, `/login?next=${encodeURIComponent(`/${slug}`)}`));
    if (done) return setNote(t.starVote.limit);
    if (paying && (state.balance ?? 0) < cost) return setNote(fmt(t.starVote.coins, { n: cost }));
    if (paying && !confirming) return setConfirming(true);
    setConfirming(false);
    setBusy(true);
    try {
      const res = await fetch("/api/stars/vote", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id, pay: paying }) });
      const out = (await res.json().catch(() => null)) as { ok?: boolean; error?: string; paid?: boolean; state?: VoteState; achievements?: Unlocked[] } | null;
      if (out?.state) setState(out.state);
      if (out?.ok) {
        setNote(out.paid ? fmt(t.starVote.paidThanks, { n: cost }) : t.starVote.thanks);
        if (out.paid) refreshCoins();
        announceAchievements(out.achievements);
      } else if (out?.error === "coins") setNote(fmt(t.starVote.coins, { n: cost }));
      else if (out?.error === "limit") setNote(t.starVote.limit);
    } finally {
      setBusy(false);
    }
  };

  const standing = state ? (state.rank ? fmt(t.starVote.standing, { rank: state.rank, n: state.votes }) : t.starVote.noVotes) : t.starVote.title;
  const label = guest ? t.starVote.vote : confirming ? fmt(t.starVote.confirm, { n: cost }) : paying ? fmt(t.starVote.voteAgain, { n: cost }) : t.starVote.vote;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={vote}
        disabled={!state || busy}
        aria-label={`${fmt(t.starVote.voteFor, { name })} · ${standing}`}
        title={`${t.starVote.title} · ${standing}${paying ? ` · ${fmt(t.starVote.rules, { n: cost })}` : ""}`}
        className={cn(
          "flex h-[38px] items-center gap-2 rounded-full pl-3 pr-1.5 text-[13.5px] transition disabled:opacity-60",
          confirming
            ? "bg-gradient-to-b from-[#ffd877] to-[#e9a93a] font-medium text-[#3a2304]"
            : paying
              ? "chip text-soft hover:text-fg"
              : "bg-[#f6c453]/15 text-[#f6c453] hover:bg-[#f6c453]/25",
        )}
      >
        {paying && !confirming ? <SweetCoin size={16} /> : <Trophy size={16} />}
        <span className="whitespace-nowrap">{label}</span>
        <span className={cn("grid h-[26px] min-w-[26px] place-items-center rounded-full px-2 text-[12px] tabular-nums", confirming ? "bg-black/15" : "bg-white/10 text-fg")}>
          {state ? state.votes.toLocaleString(lang) : "·"}
        </span>
      </button>
      {note && (
        <p role="status" className="absolute left-0 top-full z-10 mt-2 whitespace-nowrap rounded-full bg-white px-3 py-1.5 text-[12.5px] text-black shadow-lg">
          {note}
        </p>
      )}
    </div>
  );
}
